import React, { useState, useContext, useRef, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useNavigate, useParams } from 'react-router-dom';
import { Toast, ToastContainer } from 'react-bootstrap';
import AuthContext from '../functions/AuthContext';
import '../styles/AppLayoutSideBar.css';
import '../styles/Common.css';
import LogoPrincipal from './logos';
import { getRoles } from '../functions/authUtils';
import api from '../functions/api';
import EditThreadForm from './forms/EditThreadForm';
import DeleteThreadConfirm from './forms/DeleteThreadConfirm';

// Iconos
import { ReactComponent as IconOptions } from "../assets/icons/icon-options.svg";
import { ReactComponent as IconAnalytics } from "../assets/icons/icon-analytics.svg";
import { ReactComponent as IconCalendar } from "../assets/icons/icon-calendar.svg";
import { ReactComponent as IconChat } from "../assets/icons/icon-chat.svg";
import { ReactComponent as IconDots } from "../assets/icons/icon-dots.svg";
import { ReactComponent as IconDropDown } from "../assets/icons/icon-dropdown.svg";
import { ReactComponent as IconPlus } from "../assets/icons/icon-plus.svg";
import { ReactComponent as IconShield } from "../assets/icons/icon-shield.svg";
import { ReactComponent as IconUser } from "../assets/icons/icon-user.svg";

export const AppLayoutSideBar = () => {
  const { logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const { uuid_thread } = useParams();

  const [adminPrivileges, setAdminPrivileges] = useState(false);
  const [PlanificationsPrivileges, setPlanificationsPrivileges] = useState(false);
  const [AnalyticsPrivileges, setAnalyticsPrivileges] = useState(false);

  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [threads, setThreads] = useState([]);

  // dropdown usuario
  const [openUser, setOpenUser] = useState(false);
  const userMenuRef = useRef(null);

  // modales de edición/eliminación
  const [editData, setEditData] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [deleteData, setDeleteData] = useState(null);
  const [showDelete, setShowDelete] = useState(false);

  // toasts
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');
  const [showToast, setShowToast] = useState(false);

  const showToastSuccess = useCallback((msg) => {
    setToastMessage(msg);
    setToastVariant('success');
    setShowToast(true);
  }, []);

  const showToastError = useCallback((msg) => {
    setToastMessage(msg);
    setToastVariant('danger');
    setShowToast(true);
  }, []);

  const handleLogout = () => {
    setOpenUser(false);
    logout();
    navigate('/');
    showToastSuccess('Sesión finalizada');
  };

  // Si está colapsado y tocas un acordeón, primero expandimos y abrimos el acordeón
  const openOrToggle = useCallback((setter) => {
    if (sidebarCollapsed) {
      setSidebarCollapsed(false);
      setter(true);
    } else {
      setter((v) => !v);
    }
  }, [sidebarCollapsed]);

  const fetchRoles = useCallback(() => {
    try {
      const roles = getRoles();
      setAdminPrivileges(Array.isArray(roles) && roles.includes('admin'));
      setPlanificationsPrivileges(Array.isArray(roles) && (roles.includes('admin') || roles.includes('planification')));
      setAnalyticsPrivileges(Array.isArray(roles) && (roles.includes('admin') || roles.includes('analytics')));
    } catch {
      setAdminPrivileges(false);
      setPlanificationsPrivileges(false);
      setAnalyticsPrivileges(false);
      showToastError('Error al cargar los roles');
    }
  }, [showToastError]);

  // cargar threads
  const fetchThreads = useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      const idUser = localStorage.getItem("id_user");
      const r = await api.get(`/langgraph/threads/${idUser}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setThreads(r.data || []);
    } catch {
      showToastError("No se pudieron cargar las conversaciones");
    }
  }, [setThreads, showToastError]);

  useEffect(() => {
    fetchRoles();
    fetchThreads();

    const handleClickOutside = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setOpenUser(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [fetchRoles, showToastError, fetchThreads]);

  // recarga la lista cuando cambia el uuid_thread (p.e., al crear default)
  useEffect(() => {
    fetchThreads();
  }, [uuid_thread, fetchThreads]);

  // sub-menús
  const [showAdminSub, setShowAdminSub] = useState(false);
  const [showConversationsSub, setShowConversationsSub] = useState(false);

  return (
    <>
      <div id="FullPageWrapper" className={sidebarCollapsed ? 'collapsed' : ''}>
        <aside className="mc-sidebar">
          <div className={`mc-sidebar__top ${sidebarCollapsed ? 'collapsed' : ''}`}>
            <a href="/" className="mc-logo">
              <LogoPrincipal
                size={0.7}
                color="var(--text-light)"
                prefix={sidebarCollapsed ? "_small" : ""}
              />
            </a>

            <button
              className="icon-btn mc-sidebar__toggle"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
              aria-label="Contraer/Expandir barra lateral"
              title={sidebarCollapsed ? 'Expandir' : 'Contraer'}
            >
              <IconOptions className="mc-link__icon" aria-hidden="true" />
            </button>
          </div>

          <nav className="mc-nav">
            <ul>
              <li className={`mc-accordion ${showConversationsSub ? 'open' : ''}`}>
                <button
                  className="mc-accordion__btn"
                  onClick={() => openOrToggle(setShowConversationsSub)}
                >
                  <div className='mc-buttons'>
                    <IconChat className="mc-link__icon" aria-hidden="true" />
                    <span className="mc-link__label">Conversaciones</span>
                  </div>
                  <div className={`mc-collapsable ${sidebarCollapsed ? 'd-none' : ''}`}>
                    <IconDropDown className="mc-link__icon" aria-hidden="true" />
                  </div>
                </button>
                <ul className="mc-submenu">
                  {threads.map((t) => (
                    <li key={t.uuid} className="flex items-center justify-between">
                      <NavLink to={`/chat/${t.uuid}`} className="mc-sublink flex-grow">
                        {t.nombre}
                      </NavLink>
                      <div className="dropdown-dots">
                        <button className="icon-btn" data-bs-toggle="dropdown">
                          <IconDots className="mc-link__icon" aria-hidden="true" />
                        </button>
                        <ul className="dropdown-menu">
                          <li>
                            <button className="dropdown-item" onClick={() => { setEditData(t); setShowEdit(true); }}>
                              Editar
                            </button>
                          </li>
                          <li>
                            <button className="dropdown-item text-danger" onClick={() => { setDeleteData(t); setShowDelete(true); }}>
                              Eliminar
                            </button>
                          </li>
                        </ul>
                      </div>
                    </li>
                  ))}
                  <li>
                    <button
                      className="mc-link"
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem("access_token");
                          const idUser = localStorage.getItem("id_user");
                          const r = await api.post(
                            `/langgraph/threads/${idUser}`,
                            { name: "Nueva conversación" },
                            { headers: { Authorization: `Bearer ${token}` } }
                          );

                          if (r.status === 200) {
                            // navegar al nuevo uuid
                            navigate(`/chat/${r.data.uuid}`);
                            await fetchThreads(); // refresca lista
                          }
                        } catch {
                          showToastError("Error creando conversación");
                        }
                      }}
                    >
                      <IconPlus className="mc-link__icon" aria-hidden="true" />
                      <span className="mc-link__label">Nueva Conversación</span>
                    </button>
                  </li>
                </ul>
              </li>

              {PlanificationsPrivileges && (
                <li>
                  <NavLink to="/planificaciones" className="mc-link">
                    <IconCalendar className="mc-link__icon" aria-hidden="true" />
                    <span className="mc-link__label">Planificaciones</span>
                  </NavLink>
                </li>
              )}

              {AnalyticsPrivileges && (
                <li>
                  <NavLink to="/analiticas" className="mc-link">
                    <IconAnalytics className="mc-link__icon" role="img" aria-label="Analíticas" />
                    <span className="mc-link__label">Analíticas</span>
                  </NavLink>
                </li>
              )}
            </ul>
          </nav>

          <div className="mc-sidebar__bottom" ref={userMenuRef}>
            <ul>
              {adminPrivileges && (
                <li className={`mc-accordion ${showAdminSub ? 'open' : ''}`}>
                  <button className="mc-accordion__btn" onClick={() => openOrToggle(setShowAdminSub)}>
                    <div className='mc-buttons'>
                      <IconShield className="mc-link__icon" aria-hidden="true" />
                      <span className="mc-link__label">Panel Administración</span>
                    </div>
                    <div className={`mc-collapsable ${sidebarCollapsed ? 'd-none' : ''}`}>
                      <IconDropDown className="mc-link__icon" aria-hidden="true" />
                    </div>
                  </button>
                  <ul className="mc-submenu">
                    <li><NavLink to="/admin/users" className="mc-sublink">Usuarios</NavLink></li>
                    <li><NavLink to="/admin/roles" className="mc-sublink">Roles</NavLink></li>
                  </ul>
                </li>
              )}

              <li className="mc-accordion">
                <button className="mc-accordion__btn" onClick={() => openOrToggle(setOpenUser)}>
                  <div className='mc-buttons'>
                    <IconUser className="mc-link__icon" aria-hidden="true" />
                    <span className="mc-user__label">Cuenta</span>
                  </div>
                  <div className={`mc-collapsable ${sidebarCollapsed ? 'd-none' : ''}`}>
                    <IconDropDown className="mc-link__icon" aria-hidden="true" />
                  </div>
                </button>

                {openUser && (
                  <ul className="user-dropdown left">
                    <li><button onClick={() => { setOpenUser(false); navigate('/perfil'); }}>Perfil</button></li>
                    <li><button onClick={handleLogout}>Cerrar sesión</button></li>
                  </ul>
                )}
              </li>
            </ul>
          </div>
        </aside>

        <main className="mc-content">
          <Outlet />
        </main>

        <ToastContainer position="bottom-end" className="p-3">
          <Toast
            bg={toastVariant === 'success' ? 'success' : 'danger'}
            show={showToast}
            onClose={() => setShowToast(false)}
            delay={5000}
            autohide
          >
            <Toast.Header>
              <strong className="me-auto">{toastVariant === 'success' ? 'Éxito' : 'Error'}</strong>
            </Toast.Header>
            <Toast.Body>{toastMessage}</Toast.Body>
          </Toast>
        </ToastContainer>
      </div>

      <EditThreadForm
        data={editData}
        show={showEdit}
        handleClose={() => setShowEdit(false)}
        onUpdated={() => {
          fetchThreads();
        }}
      />

      <DeleteThreadConfirm
        data={deleteData}
        show={showDelete}
        handleClose={() => setShowDelete(false)}
        onDeleted={async (uuidThread) => {
          setThreads(prev => prev.filter(th => th.uuid !== uuidThread));
          await fetchThreads();
          if (String(uuidThread) === String(uuid_thread)) {
            navigate("/chat");
          }
          setShowDelete(false);
        }}
      />
    </>
  );
};
